import { useQuery, useQueries, useInfiniteQuery, useMutation, useQueryClient } from '@tanstack/react-query'
import { useMemo } from 'react'
import { apiService } from '@/services/apiService'
import { usePools } from '@/hooks/use-pools'
import type { components } from '../../../../types/flex-api'

type Pool = components['schemas']['Pool']

export interface SystemHistoryEvent {
  poolID: string
  timestamp: string
  eventType: 'INSERT' | 'DELETE'
}

const SYSTEMS_PAGE_LIMIT = 300

export function useSystemsByPool(poolID: string) {
  const query = useInfiniteQuery({
    queryKey: ['systems', poolID],
    queryFn: async ({ pageParam }) => {
      const res = await apiService.systems.getByPoolPaginated(poolID, {
        limit: SYSTEMS_PAGE_LIMIT,
        offset: pageParam as number,
      })
      if (!res.success) throw new Error(res.error ?? 'Failed to fetch systems')
      return res.data!
    },
    initialPageParam: 0,
    getNextPageParam: (lastPage, allPages) =>
      lastPage.metadata.hasMore
        ? allPages.reduce((sum, p) => sum + p.systems.length, 0)
        : undefined,
    enabled: !!poolID,
  })

  const systems = useMemo(
    () => query.data?.pages.flatMap((p) => p.systems) ?? [],
    [query.data]
  )
  const totalCount = query.data?.pages[0]?.metadata.counts.totalCount

  return { ...query, systems, totalCount }
}

export function useAllSystemIdsByPool(poolID: string) {
  return useQuery({
    queryKey: ['system-ids', poolID],
    queryFn: async () => {
      const res = await apiService.systems.getSystemIds(poolID)
      if (!res.success) throw new Error(res.error ?? 'Failed to fetch system IDs')
      return res.data ?? []
    },
    enabled: !!poolID,
  })
}

export function useSystemHistory(systemID: string) {
  return useQuery({
    queryKey: ['system-history', systemID],
    queryFn: async () => {
      const res = await apiService.systems.getHistory(systemID)
      if (!res.success) throw new Error(res.error ?? 'Failed to fetch system history')
      const events = ((res.data as any)?.events ?? []) as SystemHistoryEvent[]
      return events
        .slice()
        .sort((a, b) => new Date(b.timestamp).getTime() - new Date(a.timestamp).getTime())
    },
    enabled: !!systemID,
  })
}

export function useAddSystem() {
  const qc = useQueryClient()
  return useMutation({
    mutationFn: async ({
      poolID,
      systemID,
      activeFrom,
    }: {
      poolID: string
      systemID: string
      activeFrom?: string
    }) => {
      const res = await apiService.systems.add(poolID, systemID, activeFrom)
      if (!res.success) throw new Error(res.error ?? 'Failed to add system')
    },
    onSuccess: (_, { poolID, systemID }) => {
      qc.invalidateQueries({ queryKey: ['systems', poolID] })
      qc.invalidateQueries({ queryKey: ['system-owners', systemID] })
    },
  })
}

export function useRemoveSystem() {
  const qc = useQueryClient()
  return useMutation({
    mutationFn: async ({ poolID, systemID }: { poolID: string; systemID: string }) => {
      const res = await apiService.systems.remove(poolID, systemID)
      if (!res.success) throw new Error(res.error ?? 'Failed to remove system')
    },
    onSuccess: (_, { poolID }) => qc.invalidateQueries({ queryKey: ['systems', poolID] }),
  })
}

export function useAllSystemIds(enabled: boolean) {
  const { data: pools = [] } = usePools()

  const queries = useQueries({
    queries: pools.map((pool) => ({
      queryKey: ['system-ids', pool.poolID],
      queryFn: async (): Promise<string[]> => {
        const res = await apiService.systems.getSystemIds(pool.poolID)
        if (!res.success) return []
        return res.data ?? []
      },
      enabled: enabled && pools.length > 0,
      staleTime: 5 * 60 * 1000,
    })),
  })

  const systemIds = useMemo(() => {
    const seen = new Set<string>()
    for (const q of queries) {
      for (const id of q.data ?? []) seen.add(id)
    }
    return Array.from(seen).sort()
  }, [queries])

  const isLoading = queries.length > 0 && queries.some((q) => q.isFetching)
  const hasAnyData = queries.some((q) => q.data !== undefined)

  return { systemIds, isLoading, hasAnyData }
}

export function usePoolsBySystem(systemID: string) {
  return useQuery({
    queryKey: ['system-owners', systemID],
    queryFn: async () => {
      const res = await apiService.systems.getPoolsBySystem(systemID)
      if (!res.success) {
        if (res.errorCode === 'NOT_FOUND') return []
        throw new Error(res.error ?? 'Failed to fetch pools for system')
      }
      return (res.data as string[]) ?? []
    },
    enabled: !!systemID,
  })
}

export interface MultiPoolMember {
  systemID: string
  poolIDs: string[]
  poolCount: number
}

export function useAllPoolSystems(pools: Pool[]) {
  const idQueries = useQueries({
    queries: pools.map((p) => ({
      queryKey: ['system-ids', p.poolID] as const,
      queryFn: async () => {
        const res = await apiService.systems.getSystemIds(p.poolID)
        if (!res.success) throw new Error(res.error ?? 'Failed to fetch system IDs')
        return res.data ?? []
      },
      enabled: pools.length > 0,
    })),
  })

  const systemPoolMap = useMemo(() => {
    const map: Record<string, string[]> = {}
    idQueries.forEach((q, i) => {
      if (!q.data) return
      q.data.forEach((sysID: string) => {
        if (!map[sysID]) map[sysID] = []
        map[sysID].push(pools[i].poolID)
      })
    })
    return map
  }, [idQueries, pools])

  const multiPoolMembers = useMemo((): MultiPoolMember[] =>
    Object.entries(systemPoolMap)
      .filter(([, ids]) => ids.length >= 2)
      .map(([systemID, poolIDs]) => ({ systemID, poolIDs, poolCount: poolIDs.length }))
      .sort((a, b) => b.poolCount - a.poolCount),
    [systemPoolMap]
  )

  return {
    idsLoading: idQueries.some((q) => q.isPending),
    idsErrors: idQueries.filter((q) => q.isError).length,
    systemPoolMap,
    multiPoolMembers,
  }
}
